import { lazy } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

// project-imports
import MainLayout from 'layout/MainLayout';
import CommonLayout from 'layout/CommonLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RoleGuard from 'utils/route-guard/RoleGuard';
import IncentiveGuard, { homePathFor } from 'utils/route-guard/IncentiveGuard';
import useAuth from 'hooks/useAuth';
import AccountProfile from 'pages/apps/users/userDetails/account';
import TabPassword from 'sections/apps/profiles/account/TabPassword';
const AccountTabAdvanced = Loadable(
  lazy(() => import('sections/apps/profiles/account/TabAdvanced')),
);
const AccountTabPersonal = Loadable(
  lazy(() => import('sections/apps/profiles/account/TabPersonal')),
);

const MaintenanceError = Loadable(
  lazy(() => import('pages/maintenance/error/404')),
);
const MaintenanceError500 = Loadable(
  lazy(() => import('pages/maintenance/error/500')),
);
const MaintenanceUnderConstruction = Loadable(
  lazy(() => import('pages/maintenance/under-construction/under-construction')),
);
const MaintenanceComingSoon = Loadable(
  lazy(() => import('pages/maintenance/coming-soon/coming-soon')),
);

const DashboardDefault = Loadable(
  lazy(() => import('pages/dashboard/default')),
);

const Profile = Loadable(lazy(() => import('pages/profile')));

// render - sample page
// const SamplePage = Loadable(
//   lazy(() => import("pages/extra-pages/sample-page"))
// );

const AppUsersList = Loadable(lazy(() => import('pages/apps/users/list')));
const AppStoreList = Loadable(lazy(() => import('pages/apps/stores/list')));
const AppWorkflowsList = Loadable(
  lazy(() => import('pages/apps/workflows/list')),
);
const AppWorkflowCreate = Loadable(
  lazy(() => import('pages/apps/workflows/create')),
);
const AppWorkflowEdit = Loadable(
  lazy(() => import('pages/apps/workflows/edit')),
);
const AppWorkflowDiagram = Loadable(
  lazy(() => import('pages/apps/workflows/diagram')),
);
const AppWorkflowDashboard = Loadable(
  lazy(() => import('pages/apps/workflows/dashboardRedirect')),
);
const AppTicketsList = Loadable(lazy(() => import('pages/apps/tickets/list')));
const AppTicketView = Loadable(lazy(() => import('pages/apps/tickets/view')));
const AppTicketCreate = Loadable(lazy(() => import('pages/apps/tickets/create')));
const AppTicketsByWorkflow = Loadable(
  lazy(() => import('pages/apps/tickets/byWorkflow')),
);
const AppTeamsList = Loadable(lazy(() => import('pages/apps/teams/list')));
const AppRolesList = Loadable(lazy(() => import('pages/apps/roles/list')));
const AppViolations = Loadable(lazy(() => import('pages/apps/violations')));
const AppHvcConfig = Loadable(lazy(() => import('pages/apps/hvcConfig')));
const AppIncentiveRedemptions = Loadable(
  lazy(() => import('pages/apps/traders/incentiveRedemptions')),
);
const AppRedemptionsList = Loadable(
  lazy(() => import('pages/apps/traders/redemptionsList')),
);
const AppTradersApprovals = Loadable(
  lazy(() => import('pages/apps/traders/approvals')),
);
const AppRcMapping = Loadable(
  lazy(() => import('pages/apps/traders/rcMapping')),
);
const AppTradersDashboard = Loadable(
  lazy(() => import('pages/apps/traders/dashboard')),
);
const AppStoreIncentivesTargetUpload = Loadable(
  lazy(() => import('pages/apps/storeIncentives/targetUpload')),
);
const AppStoreIncentivesAssignedStaffs = Loadable(
  lazy(() => import('pages/apps/storeIncentives/assignedStaffs')),
);
const AppStoreIncentivesDashboard = Loadable(
  lazy(() => import('pages/apps/storeIncentives/dashboard')),
);
const AppStaffIncentivesClusterDashboard = Loadable(
  lazy(() => import('pages/apps/staffIncentives/clusterwise/dashboard')),
);
const AppStaffIncentivesClusterTargets = Loadable(
  lazy(() => import('pages/apps/staffIncentives/clusterwise/targets')),
);
const AppStaffIncentivesSlabDashboard = Loadable(
  lazy(() => import('pages/apps/staffIncentives/slabwise/dashboard')),
);
const AppStaffIncentivesSlabTargets = Loadable(
  lazy(() => import('pages/apps/staffIncentives/slabwise/targets')),
);
// Root landing: the accounts team goes to the first toggled page they have
// enabled; everyone else to the app default.
const DefaultRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={homePathFor(user)} replace />;
};

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <DefaultRedirect />,
        },
        {
          path: 'dashboard',
          element: <DashboardDefault />,
        },
        {
          path: 'profile',
          element: <Profile />,
        },
        {
          path: 'users',
          element: (
            <RoleGuard>
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              path: 'list',
              element: <AppUsersList />,
            },
            {
              path: 'list/:id',
              element: <AccountProfile />,
              children: [
                {
                  path: 'personal',
                  element: <AccountTabPersonal />,
                },
                {
                  path: 'advanced',
                  element: <AccountTabAdvanced />,
                },
                {
                  path: 'my-account',
                  element: <div>3</div>,
                },
                {
                  path: 'password',
                  element: <TabPassword />,
                },
                {
                  path: 'role',
                  element: <div>5</div>,
                },
                {
                  path: 'settings',
                  element: <div>6</div>,
                },
              ],
            },
          ],
        },

        {
          path: 'stores',
          element: (
            <RoleGuard>
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              path: 'list',
              element: <AppStoreList />,
            },
          ],
        },
        {
          path: 'workflows',
          children: [
            {
              path: 'list',
              element: (
                <RoleGuard>
                  <AppWorkflowsList />
                </RoleGuard>
              ),
            },
            {
              path: 'create',
              element: (
                <RoleGuard>
                  <AppWorkflowCreate />
                </RoleGuard>
              ),
            },
            {
              path: 'edit/:workflowId',
              element: (
                <RoleGuard>
                  <AppWorkflowEdit />
                </RoleGuard>
              ),
            },
            {
              path: 'diagram/:workflowId',
              element: (
                <RoleGuard>
                  <AppWorkflowDiagram />
                </RoleGuard>
              ),
            },
            {
              // Per-workflow analytics dashboard — overseers (handlers/viewers)
              // may view it, so it is NOT restricted to admins.
              path: 'dashboard/:workflowId',
              element: <AppWorkflowDashboard />,
            },
          ],
        },
        {
          path: 'tickets',
          children: [
            {
              path: 'list',
              element: <AppTicketsList />,
            },
            {
              path: 'create',
              element: <AppTicketCreate />,
            },
            {
              path: 'workflow/:workflowId',
              element: <AppTicketsByWorkflow />,
            },
            {
              path: 'view/:ticketId',
              element: <AppTicketView />,
            },
          ],
        },
        {
          path: 'teams',
          element: (
            <RoleGuard>
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              path: 'list',
              element: <AppTeamsList />,
            },
          ],
        },
        {
          path: 'roles',
          element: (
            <RoleGuard>
              <Outlet />
            </RoleGuard>
          ),
          children: [
            {
              path: 'list',
              element: <AppRolesList />,
            },
          ],
        },
        {
          path: 'violations',
          element: (
            <RoleGuard>
              <AppViolations />
            </RoleGuard>
          ),
        },
        {
          // Super-admin only: this decides which invoices are flagged and how
          // much staff are paid, so it is narrower than the rest of the module.
          path: 'hvc-config',
          element: (
            <RoleGuard roles={['SUPER_ADMIN']}>
              <AppHvcConfig />
            </RoleGuard>
          ),
        },
        {
          path: 'traders',
          element: (
            <IncentiveGuard section="traders">
              <Outlet />
            </IncentiveGuard>
          ),
          children: [
            {
              path: 'dashboard',
              element: (
                <IncentiveGuard section="tradersDashboard">
                  <AppTradersDashboard />
                </IncentiveGuard>
              ),
            },
            {
              path: 'incentive-redemptions',
              element: (
                <IncentiveGuard section="tradersRedemptions">
                  <AppIncentiveRedemptions />
                </IncentiveGuard>
              ),
            },
            {
              path: 'incentive-redemptions/:programId',
              element: (
                <IncentiveGuard section="tradersRedemptions">
                  <AppRedemptionsList />
                </IncentiveGuard>
              ),
            },
            {
              path: 'rc-mapping',
              element: (
                <IncentiveGuard section="tradersRcMapping">
                  <AppRcMapping />
                </IncentiveGuard>
              ),
            },
            {
              path: 'approvals',
              element: (
                <IncentiveGuard section="tradersApprovals">
                  <AppTradersApprovals />
                </IncentiveGuard>
              ),
            },
          ],
        },
        {
          path: 'store-incentives',
          element: (
            <IncentiveGuard section="store">
              <Outlet />
            </IncentiveGuard>
          ),
          children: [
            {
              path: 'dashboard',
              element: (
                <IncentiveGuard section="storeDashboard">
                  <AppStoreIncentivesDashboard />
                </IncentiveGuard>
              ),
            },
            {
              path: 'target-upload',
              element: (
                <IncentiveGuard section="storeTargetUpload">
                  <AppStoreIncentivesTargetUpload />
                </IncentiveGuard>
              ),
            },
            {
              path: 'assigned-staffs',
              element: (
                <IncentiveGuard section="storeAssignedStaffs">
                  <AppStoreIncentivesAssignedStaffs />
                </IncentiveGuard>
              ),
            },
          ],
        },
        {
          path: 'staff-incentives',
          element: (
            <IncentiveGuard section="staff">
              <Outlet />
            </IncentiveGuard>
          ),
          children: [
            {
              path: 'clusterwise',
              children: [
                {
                  path: 'dashboard',
                  element: (
                    <IncentiveGuard section="clusterDashboard">
                      <AppStaffIncentivesClusterDashboard />
                    </IncentiveGuard>
                  ),
                },
                {
                  path: 'targets',
                  element: (
                    <IncentiveGuard section="clusterTargets">
                      <AppStaffIncentivesClusterTargets />
                    </IncentiveGuard>
                  ),
                },
              ],
            },
            {
              path: 'slabwise',
              children: [
                {
                  path: 'dashboard',
                  element: (
                    <IncentiveGuard section="slabDashboard">
                      <AppStaffIncentivesSlabDashboard />
                    </IncentiveGuard>
                  ),
                },
                {
                  path: 'targets',
                  element: (
                    <IncentiveGuard section="slabTargets">
                      <AppStaffIncentivesSlabTargets />
                    </IncentiveGuard>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '/maintenance',
      element: <CommonLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />,
        },
        {
          path: '500',
          element: <MaintenanceError500 />,
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />,
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />,
        },
      ],
    },
  ],
};

export default MainRoutes;
